import type { CanonicalProduct, ProductId, ProductOptionGroup } from '../menu/product.ts';

export interface ProductOptionSelectionInput {
  readonly optionGroupId: string;
  readonly optionIds: readonly string[];
}

export interface ProductSelectionInput {
  readonly lineId: string;
  readonly productId: ProductId;
  readonly quantity: number;
  readonly selections: readonly ProductOptionSelectionInput[];
  readonly note?: string | null;
}

export interface OrderSelectedOptionSnapshot {
  readonly optionGroupId: string;
  readonly optionId: string;
  readonly name: string;
  readonly priceDeltaMinor: number;
}

export interface OrderLineSnapshot {
  readonly lineId: string;
  readonly productId: ProductId;
  readonly quantity: number;
  readonly selectorKeys: readonly string[];
  readonly productSnapshot: {
    readonly customerName: string;
    readonly operationsName: string;
    readonly selectedOptions: readonly OrderSelectedOptionSnapshot[];
  };
  readonly note: string | null;
}

const requiredMinimum = (group: ProductOptionGroup): number =>
  group.required ? Math.max(1, group.min) : group.min;

export function buildOrderLineSnapshot(
  product: CanonicalProduct,
  input: ProductSelectionInput,
): OrderLineSnapshot {
  if (input.productId !== product.id) throw new Error('PRODUCT_SELECTION_ID_MISMATCH');
  if (product.status !== 'active') throw new Error('PRODUCT_NOT_ACTIVE');
  if (!Number.isSafeInteger(input.quantity) || input.quantity <= 0) throw new Error('ORDER_LINE_QUANTITY_INVALID');

  const selectionsByGroup = new Map<string, string[]>();
  for (const selection of input.selections) {
    const current = selectionsByGroup.get(selection.optionGroupId) ?? [];
    selectionsByGroup.set(selection.optionGroupId, [...current, ...selection.optionIds]);
  }

  const groupsById = new Map(product.optionGroups.map((group) => [group.id, group]));
  for (const [groupId, optionIds] of selectionsByGroup) {
    const group = groupsById.get(groupId);
    if (!group) throw new Error('PRODUCT_OPTION_UNKNOWN');
    if (!group.enabled && optionIds.length > 0) throw new Error('PRODUCT_OPTION_DISABLED');
  }

  const selectedOptions: OrderSelectedOptionSnapshot[] = [];
  const orderedGroups = [...product.optionGroups].sort((a, b) => a.order - b.order);
  for (const group of orderedGroups) {
    const selectedIds = selectionsByGroup.get(group.id) ?? [];
    if (!group.enabled) {
      if (selectedIds.length > 0) throw new Error('PRODUCT_OPTION_DISABLED');
      continue;
    }
    if (selectedIds.length < requiredMinimum(group) || selectedIds.length > group.max) {
      throw new Error('PRODUCT_OPTION_COUNT_INVALID');
    }
    for (const optionId of selectedIds) {
      const option = group.options.find((candidate) => candidate.id === optionId);
      if (!option) throw new Error('PRODUCT_OPTION_UNKNOWN');
      if (!option.enabled) throw new Error('PRODUCT_OPTION_DISABLED');
      selectedOptions.push({
        optionGroupId: group.id,
        optionId: option.id,
        name: option.name,
        priceDeltaMinor: option.priceDeltaMinor,
      });
    }
  }

  return {
    lineId: input.lineId,
    productId: product.id,
    quantity: input.quantity,
    selectorKeys: [...product.pricing.selectorKeys],
    productSnapshot: {
      customerName: product.names.customer,
      operationsName: product.names.operations,
      selectedOptions,
    },
    note: input.note?.trim() || null,
  };
}
